import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);

  const categories = ['Plumbing', 'Electrical', 'Tools', 'Pipes', 'Fittings', 'Lighting'];

  // Sample search suggestions based on products
  const searchSuggestions = [
    'Angle Valve',
    'Circuit Breaker',
    'Pipe Wrench',
    'LED Panel Light',
    'PVC Pipe',
    'Water Heater',
    'Voltage Tester',
    'Switch Outlet',
    'Conduit Pipe',
    'Copper Fitting'
  ];

  const filteredSuggestions = searchSuggestions.filter(suggestion =>
    suggestion.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Find the product section and scroll to it
      const productSection = document.getElementById('featured');
      if (productSection) {
        productSection.scrollIntoView({ behavior: 'smooth' });
      }
      setShowSearchSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setShowSearchSuggestions(false);
    
    // Scroll to featured products section
    const productSection = document.getElementById('featured');
    if (productSection) {
      productSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className="bg-white border-b sticky top-0 z-50">
      {/* Top Bar - Orange Theme Restored */}
      <div className="bg-orange-500 text-white py-2 text-sm">
        <div className="container mx-auto px-4 text-center">
          🚚 Free delivery within Kakamega Municipality for orders above KSH 5,000
        </div>
      </div>

      {/* Main Header */}
      <nav className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">K</span>
            </div>
            <span className="text-2xl font-bold text-gray-800">Kassim Hardware</span>
          </div>

          {/* User Actions */}
          <div className="hidden md:flex items-center space-x-6">
            <a href="#/admin" className="flex flex-col items-center text-gray-600 hover:text-orange-500">
              <span className="text-lg">🔧</span>
              <span className="text-xs">Admin</span>
            </a>
            <button className="flex flex-col items-center text-gray-600 hover:text-orange-500">
              <span className="text-lg">👤</span>
              <span className="text-xs">Account</span>
            </button>
            <button className="flex flex-col items-center text-gray-600 hover:text-orange-500">
              <span className="text-lg">❤️</span>
              <span className="text-xs">Wishlist</span>
            </button>
            <button className="flex flex-col items-center text-gray-600 hover:text-orange-500">
              <span className="text-lg">🛒</span>
              <span className="text-xs">Cart</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <div className="w-6 h-6 flex flex-col justify-between">
              <span className="w-full h-0.5 bg-gray-800 rounded"></span>
              <span className="w-full h-0.5 bg-gray-800 rounded"></span>
              <span className="w-full h-0.5 bg-gray-800 rounded"></span>
            </div>
          </button>
        </div>

        {/* Search Bar - Below Brand Name */}
        <div className="mt-4 max-w-2xl mx-auto">
          <form onSubmit={handleSearchSubmit} className="relative">
            <input
              type="text"
              placeholder="Search products, brands and categories..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 text-sm"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSearchSuggestions(true);
              }}
              onFocus={() => setShowSearchSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSearchSuggestions(false), 200)}
            />
            <button 
              type="submit"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors text-sm"
            >
              Search
            </button>

            {/* Search Suggestions */}
            <AnimatePresence>
              {showSearchSuggestions && searchQuery && filteredSuggestions.length > 0 && (
                <motion.div
                  className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 z-50 max-h-60 overflow-y-auto search-suggestions"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  {filteredSuggestions.map((suggestion, index) => (
                    <div
                      key={suggestion}
                      className="px-4 py-2 hover:bg-orange-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                      onMouseDown={() => handleSuggestionClick(suggestion)}
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-400 text-sm">🔍</span>
                        <span className="text-gray-700">{suggestion}</span>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </div>

        {/* Categories Navigation */}
        <div className="hidden md:flex justify-center space-x-8 mt-4">
          {categories.map((category) => (
            <a
              key={category}
              href={`#${category.toLowerCase()}`}
              className="text-gray-600 hover:text-orange-500 font-medium text-sm"
            >
              {category}
            </a>
          ))}
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="md:hidden bg-white border-t"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="container mx-auto px-4 py-4">
              <div className="space-y-4">
                <a
                  href="#/admin"
                  className="block text-gray-600 hover:text-orange-500 py-2 font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  🔧 Admin Panel
                </a>
                {categories.map((category) => (
                  <a
                    key={category}
                    href={`#${category.toLowerCase()}`}
                    className="block text-gray-600 hover:text-orange-500 py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {category}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
